import React, { useState } from 'react';
import { Waves, Anchor, Ruler } from 'lucide-react';

/**
 * Interactive water-depth helper shown on product detail pages.
 * Given the selected riser size and/or number of 4lb weights, it shows the
 * resulting recommended water depth, lets a shopper enter their pool depth to
 * get a suggested setup, and displays the full depth chart per riser size.
 */
export const DepthGuide = ({ guide, selectedCustomizations = {} }) => {
  const [poolDepth, setPoolDepth] = useState('');

  if (!guide || !Array.isArray(guide.risers) || guide.risers.length === 0) return null;

  const hasRiser = !!guide.riserOption;
  const hasWeights = !!guide.weightOption;

  const selectedRiserName = hasRiser ? selectedCustomizations[guide.riserOption] : guide.risers[0].name;
  const selectedWeights = hasWeights ? selectedCustomizations[guide.weightOption] : null;

  const findRiser = (name) => guide.risers.find((r) => r.name === name);

  const resolveDepth = (riser, weights) => {
    if (!riser) return { needRiser: true };
    const rows = [...(riser.rows || [])].sort((a, b) => (a.w || 0) - (b.w || 0));
    if (!rows.length) return null;
    if (!hasWeights) return { label: rows[0].label, note: rows[0].note };
    if (weights === undefined || weights === null) return { needWeights: true };
    const exact = rows.find((r) => r.w === weights);
    if (exact) return { label: exact.label, note: exact.note };
    const top = rows[rows.length - 1];
    if (weights > (top.w || 0)) return { label: top.label, note: top.note, beyond: riser.beyond };
    const lower = [...rows].reverse().find((r) => (r.w || 0) <= weights);
    return lower ? { label: lower.label, note: lower.note } : { label: rows[0].label, note: rows[0].note };
  };

  const current = resolveDepth(findRiser(selectedRiserName), selectedWeights);

  const recommend = () => {
    const D = parseFloat(poolDepth);
    if (!D || D <= 0) return undefined;
    let best = null;
    guide.risers.forEach((r) => {
      const rows = [...(r.rows || [])].sort((a, b) => (a.w || 0) - (b.w || 0));
      rows.forEach((row) => {
        if ((row.max || 0) >= D) {
          if (!best || row.max < best.max || (row.max === best.max && (row.w || 0) < (best.w || 0))) {
            best = { riser: r.name, w: row.w, max: row.max, label: row.label };
          }
        }
      });
    });
    return best; // null = no combo covers depth
  };
  const rec = poolDepth ? recommend() : undefined;

  const setupText = (riserName, w) => {
    const parts = [];
    if (hasRiser) parts.push(riserName === 'No Riser' ? 'No risers' : `${riserName} risers`);
    if (hasWeights) parts.push(`${w || 0} weight${(w || 0) === 1 ? '' : 's'}`);
    return parts.join(' + ') || 'Standard setup';
  };

  return (
    <div
      className="mb-8 rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-5 sm:p-6"
      data-testid="depth-guide-section"
    >
      <div className="flex items-center gap-2 mb-4">
        <Waves size={20} className="text-cyan-600" />
        <h3 className="text-base font-bold text-gray-900">Water Depth Guide</h3>
      </div>

      {/* Live result for the current selection */}
      <div
        className="rounded-xl bg-white border border-cyan-100 p-4 mb-4"
        data-testid="depth-guide-live-result"
      >
        {current?.needRiser ? (
          <p className="text-sm text-gray-600">Select a <span className="font-semibold">riser size</span> above to see the recommended water depth.</p>
        ) : current?.needWeights ? (
          <p className="text-sm text-gray-600">
            {hasRiser && (
              <>For <span className="font-semibold text-cyan-700">{selectedRiserName === 'No Riser' ? 'no risers' : `${selectedRiserName} risers`}</span>, </>
            )}
            set the number of <span className="font-semibold">4lb weights</span> above to see the water depth.
          </p>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Your selection works in</p>
            <p className="text-2xl font-extrabold text-gray-900" data-testid="depth-guide-live-depth">
              {current.label} <span className="text-base font-semibold text-gray-500">of water depth</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">{setupText(selectedRiserName, selectedWeights)}</p>
            {current.note && <p className="text-xs text-cyan-700 mt-1">{current.note}</p>}
            {current.beyond && <p className="text-xs text-amber-700 mt-1">{current.beyond}</p>}
          </>
        )}
      </div>

      {/* Pool-depth recommendation */}
      <div className="mb-4">
        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 mb-2">
          <Ruler size={15} className="text-cyan-600" /> Know your pool depth? Get a recommendation
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="1"
            inputMode="decimal"
            value={poolDepth}
            onChange={(e) => setPoolDepth(e.target.value)}
            placeholder="e.g. 14"
            className="w-28 px-3 py-2 rounded-xl border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm"
            data-testid="depth-guide-pool-input"
          />
          <span className="text-sm text-gray-600">inches deep</span>
        </div>
        {rec !== undefined && (
          <div className="mt-2 text-sm" data-testid="depth-guide-recommend-result">
            {rec ? (
              <p className="text-gray-800">
                Recommended setup: <span className="font-semibold text-cyan-700">{setupText(rec.riser, rec.w)}</span> — works in {rec.label}.
              </p>
            ) : (
              <p className="text-amber-700">That depth is beyond this product's maximum. Please <a href="/contact" className="underline font-medium">contact us</a> for options.</p>
            )}
          </div>
        )}
      </div>

      {/* Full depth chart */}
      <details className="group" data-testid="depth-guide-chart">
        <summary className="cursor-pointer list-none flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-800">
          <Anchor size={15} />
          View full depth chart
        </summary>
        <div className="mt-3 space-y-4">
          {guide.ballastNote && <p className="text-xs text-gray-600 italic">{guide.ballastNote}</p>}
          {guide.risers.map((r) => (
            <div key={r.name} data-testid={`depth-guide-chart-${r.name.toLowerCase().replace(/\s+/g, '-')}`}>
              {hasRiser && <p className="text-sm font-semibold text-gray-900 mb-1">{r.name}</p>}
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="flex bg-gray-50 text-gray-500 text-xs font-medium">
                  {hasWeights && <div className="w-28 px-3 py-1.5">4lb Weights</div>}
                  <div className="flex-1 px-3 py-1.5">Works in</div>
                </div>
                {[...(r.rows || [])].sort((a, b) => (a.w || 0) - (b.w || 0)).map((row, i) => (
                  <div key={i} className="flex border-t border-gray-200 text-sm">
                    {hasWeights && (
                      <div className="w-28 px-3 py-1.5 text-gray-700">{(row.w || 0)} weight{(row.w || 0) === 1 ? '' : 's'}</div>
                    )}
                    <div className="flex-1 px-3 py-1.5 text-gray-900 font-medium">
                      {row.label}
                      {row.note && <span className="block text-xs font-normal text-cyan-700">{row.note}</span>}
                    </div>
                  </div>
                ))}
              </div>
              {r.beyond && <p className="text-xs text-amber-700 mt-1">{r.beyond}</p>}
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

export default DepthGuide;
